import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { AUTH_ROUTES, PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/snowboard-shipping.jpg'),
    alt: 'snowboard shipping page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'snowboard shipping page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Snowboard <span>Shipping Services to</span> Resorts and Storage',
  description:
    'Ship your snowboard effortlessly with our reliable, affordable shipping services to destinations worldwide.',

  firstImage: {
    url: '/images/v2/pages/main-pages/snowboard-shipping/hero-image1.png',
    alt: 'snowboard-shipping'
  },

  secondImage: {
    url: '/images/v2/pages/main-pages/snowboard-shipping/hero-image2.png',
    alt: 'snowboard-shipping'
  },
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Start Shipping' }
}

export const whyWeSectionContent = {
  title: 'Why Choose ShipAndStorage for Snowboard Shipping?',
  cards: [
    {
      id: 1,
      title: 'Save Time',
      description: 'Skip the long waits at baggage claim and travel hassle-free without lugging your snowboard.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/save-time.svg',
        alt: 'Save Time service'
      }
    },
    {
      id: 2,
      title: 'Affordable Rates',
      description: 'Save up to 70% on your travel expenses by avoiding those hefty airline baggage fees.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/affordable-rates.svg',
        alt: 'Affordable Rates service'
      }
    },
    {
      id: 3,
      title: 'On-Time Delivery',
      description: 'Rest assured that your snowboard will arrive at your destination safely and promptly.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/on-time-delivery.svg',
        alt: 'On-Time Delivery service'
      }
    },
    {
      id: 4,
      title: 'Free Insurance',
      description: 'Every shipment includes complimentary coverage of up to $250 for added peace of mind.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/free-insurance.svg',
        alt: 'Free Insurance service'
      }
    },
    {
      id: 5,
      title: 'Flexible Shipping',
      description: 'Enjoy convenient choices, including door-to-door pickup or easy drop-off without any hassle.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/flexible-shipping.svg',
        alt: 'Flexible Shipping service'
      }
    },
    {
      id: 6,
      title: '24/7 Support',
      description: 'Get assistance anytime with our dedicated customer service team available around the clock.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/support.svg',
        alt: '24/7 Support service'
      }
    }
  ]
}

export const howOurSnowboardShippingServiceWorksSectionContent = {
  title: 'How Our Snowboard Shipping Service Works',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Ship Your Snowboard' },

  image: {
    src: '/images/v2/pages/main-pages/snowboard-shipping/how-our-snowboard-shipping-service-works.jpg',
    alt: 'Save Time service'
  },

  cards: [
    {
      id: 1,
      title: 'Book Your Storage',
      description: 'Book your storage by placing an order and sending your snowboard to our warehouses.'
    },
    {
      id: 2,
      title: 'Store Your Gear',
      description: 'Store your gear securely with us for up to 6 months free once your snowboard arrives.'
    },
    {
      id: 3,
      title: 'Ship When Ready',
      description:
        'Ship when ready by letting us know, and we’ll repackage and send your snowboard to your destination.'
    },
    {
      id: 4,
      title: 'Enjoy the Convenience',
      description: 'Enjoy the convenience of flexible shipping options, 24/7 security, and package protection.'
    }
  ]
}

export const helperSectionContent = {
  title: 'How to Pack and Label Your Snowboard for Shipping',

  articles: [
    {
      content: [
        {
          title: 'Packing Your Snowboard',
          points: [
            '<strong>Wrap:</strong> Use bubble wrap around your snowboard to avoid scratches.',
            '<strong>Case:</strong> Place your snowboard in a soft or hard travel case or bag for extra protection.',
            '<strong>Secure:</strong> Fasten the bag using buckles and straps.',
            '<strong>Close:</strong> Ensure all pockets are zipped or closed.',
            '<strong>Lock:</strong> For U.S. domestic shipments, use TSA-approved locks or cable ties. For international shipments, only use cable ties to lock your bag.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/packing-your-snowboard.png',
        alt: 'Packing Your Snowboard'
      }
    },
    {
      content: [
        {
          title: 'Labeling Your Snowboard',
          points: [
            '<strong>Shipping Labels:</strong> Place the shipping label and document inside the pouch, with the tracking barcode facing outward.',
            '<strong>Tag:</strong> Securely attach the pouch to the top handle of your snowboard bag.',
            '<strong>Backup:</strong> Include an extra copy of the label and shipping document inside your snowboard bag.',
            '<strong>Photo:</strong> Take a photo of your labeled snowboard, including the tracking barcode, for reference.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/labeling-your-snowboard.png',
        alt: 'Labeling Your Snowboard'
      }
    }
  ]
}

export const benefitsSectionContent = {
  title: 'Snowboard Sizing and Shipping Guidelines',
  description: 'To ensure smooth transportation, we provide clear size and weight guidelines.',

  cards: [
    {
      id: 1,
      title: 'Small Snowboard',
      points: [
        '<strong>Max Weight:</strong> 9 lbs / 4 kg',
        '<strong>Max Length:</strong> 36 inches / 90 cm',
        '<strong>Max Girth:</strong> 48 inches / 123cm'
      ],
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/small-snowboard.png',
        alt: 'Small Snowboard service'
      }
    },
    {
      id: 2,
      title: 'Large Snowboard',
      points: [
        '<strong>Max Weight:</strong> 15lbs / 7 kg',
        '<strong>Max Length:</strong> 70 inches / 178cm',
        '<strong>Max Girth:</strong> 56 inches / 142cm'
      ],
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/large-snowboard.png',
        alt: 'Large Snowboard service'
      }
    }
  ]
}

export const popularDestinationsSectionContent = {
  title: 'Popular Snowboarding Destinations We Serve',
  description: 'We makes it easy to enjoy your favorite snowboarding spots around the world.',

  cards: [
    {
      id: 1,
      title: 'Aspen, Colorado, USA',
      description:
        'Aspen offers four world-class mountains, perfect for snowboarders of all levels. From the challenging terrain of Snowmass to the scenic runs at Aspen Mountain, this destination is a must-visit for snow sports enthusiasts.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/aspen-colorado-usa.png',
        alt: 'Aspen, Colorado, USA'
      }
    },
    {
      id: 2,
      title: 'Whistler Blackcomb, Canada',
      description:
        'The largest ski resort in North America, Whistler Blackcomb, is a haven for snowboarders. With over 8,000 acres of terrain, pristine powder, and lively après-ski options, it’s a top choice for winter adventurers.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/whistler-blackcomb-canada.png',
        alt: 'Whistler Blackcomb, Canada'
      }
    },
    {
      id: 3,
      title: 'Chamonix-Mont-Blanc, France',
      description:
        "Nestled in the French Alps, Chamonix is known for its challenging off-piste terrain and breathtaking mountain views. It's a dream destination for experienced snowboarders.",
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/chamonix-mont-blanc-france.png',
        alt: 'Chamonix-Mont-Blanc, France'
      }
    },
    {
      id: 4,
      title: 'Hakuba Valley, Japan',
      description:
        'Renowned for its famous "Japow" (Japanese powder snow), Hakuba Valley features 10 ski resorts and endless opportunities for adventure. It’s a paradise for those who crave deep, soft snow.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/hakuba-valley-japan.png',
        alt: 'Hakuba Valley, Japan'
      }
    },
    {
      id: 5,
      title: 'Queenstown, New Zealand',
      description:
        'For snowboarding adventures in the Southern Hemisphere, Queenstown is unbeatable. Surrounded by dramatic mountain ranges and offering world-class snowboarding facilities, it’s a year-round destination for thrill-seekers.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/queenstown-new-zealand.png',
        alt: 'Queenstown, New Zealand'
      }
    },
    {
      id: 6,
      title: 'Zermatt, Switzerland',
      description:
        'Zermatt boasts Europe’s highest ski lift and offers access to thrilling snowboarding runs on the iconic Matterhorn. With year-round snow, it’s a favorite for snowboarding enthusiasts.',
      image: {
        src: '/images/v2/pages/main-pages/snowboard-shipping/zermatt-switzerland.png',
        alt: 'Zermatt, Switzerland'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Ready to Ship Your Snowboard?',
  description:
    'Save time, hassle, and money with ShipAndStorage snowboard shipping services. Whether you’re planning a ski trip abroad or just heading to the nearest resort, we make shipping your snowboard simple and affordable.',
  image: {
    src: '/images/v2/pages/main-pages/snowboard-shipping/promotional-banner-image.png',
    alt: 'Ready to Ship Your Snowboard?'
  },
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Get Started Now' }
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'How far in advance should I book my snowboard shipment?',
      answer: 'We recommend booking at least 3-5 days in advance to ensure smooth delivery. '
    },
    {
      id: 2,
      question: 'What if my snowboard is damaged during transit?',
      answer:
        'All shipments are covered with up to $250 in free insurance. For additional coverage, you can purchase more insurance during checkout.'
    },
    {
      id: 3,
      question: 'Can I track my shipment in real time?',
      answer: 'Yes! Our real-time tracking system allows you to monitor your snowboard every step of the way.'
    },
    {
      id: 4,
      question: 'Can I ship snowboarding gear along with my snowboard?',
      answer:
        'Absolutely! You can include boots, helmets, and other gear in your snowboard bag as long as it meets our size and weight guidelines.'
    },
    {
      id: 5,
      question: 'What happens if I need long-term storage?',
      answer: 'We offer up to six months of free storage, with options to extend at a nominal cost.'
    }
  ]
}
